import React, { useEffect, useState } from 'react'
import { Button, TextField } from '../ui'
import { useAuth } from './useAuth.js'
import { api } from '../api/client.js'

export function LoginPage() {
  const { signIn, signInWithIdp } = useAuth()
  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [hint, setHint] = useState(null)
  const [error, setError] = useState(null)

  // Fetch the short-lived discovery-gate cookie on mount. /auth/discovery
  // requires a valid one; without it the endpoint returns the "no SSO"
  // shape regardless of the email (SEC-032). Fire-and-forget — the cookie
  // is set as a side effect; response body is empty.
  useEffect(() => {
    api.authChallenge()
  }, [])

  async function handleContinue(e) {
    e.preventDefault()
    const normalized = email.trim().toLowerCase()
    if (!normalized.includes('@')) {
      setError('Enter a valid email address.')
      return
    }
    setError(null)
    setLoading(true)
    try {
      const redirectUri = `${window.location.origin}/callback`
      const discovery = await api.authDiscovery(normalized, redirectUri)
      if (discovery?.identityProvider) {
        setHint({ providerName: discovery.identityProvider, ssoRequired: !!discovery.ssoRequired })
      } else {
        // No SSO for this domain — fall through to native login. Pass
        // the typed email as a login hint so Cognito Managed Login
        // pre-fills its email field instead of asking again.
        signIn(normalized)
      }
    } catch (_) {
      // Discovery is best-effort: on network error fall back to native login.
      signIn(normalized)
    } finally {
      setLoading(false)
    }
  }

  function handleSso() {
    if (!hint?.providerName) return
    signInWithIdp(hint.providerName)
  }

  function handleReset() {
    setHint(null)
    setError(null)
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.brand}>
          <img src="/logo-mark.svg" alt="" width={24} height={24} />
          <h1 style={styles.title}>Soundcheck</h1>
        </div>
        <p style={styles.tagline}>Prepare. Align. Grow.</p>

        {!hint && (
          <form onSubmit={handleContinue} style={styles.form}>
            <label style={styles.label}>
              <span style={styles.fieldLabel}>Work email</span>
              <TextField
                type="text"
                inputMode="email"
                autoComplete="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
                autoFocus
                aria-label="Work email"
              />
            </label>
            {error && <div style={styles.error}>{error}</div>}
            <Button type="submit" size="lg" loading={loading} loadingLabel="Checking…" style={{ width: '100%' }}>
              Continue
            </Button>
          </form>
        )}

        {hint && (
          <div style={styles.form}>
            <div style={styles.hint}>
              Your organization uses single sign-on
              {hint.ssoRequired ? ' (required for all members)' : ''}.
            </div>
            <Button size="lg" onClick={handleSso} style={{ width: '100%' }}>
              Sign in with Microsoft
            </Button>
            {!hint.ssoRequired && (
              <button type="button" style={styles.link} onClick={signIn}>
                Use a password or passkey instead
              </button>
            )}
            <button type="button" style={styles.link} onClick={handleReset}>
              Use a different email
            </button>
          </div>
        )}
      </div>
    </div>
  )
}

const styles = {
  container: {
    minHeight: '100vh',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    background: '#fafaf9',
    backgroundImage: 'url(/login-bg.svg)',
    backgroundSize: 'cover',
    backgroundPosition: 'center',
  },
  card: {
    background: '#ffffff',
    border: '1px solid #e8e8e6',
    borderRadius: '12px',
    padding: '40px',
    width: '360px',
    display: 'flex',
    flexDirection: 'column',
    gap: '16px',
  },
  brand: { display: 'flex', alignItems: 'center', gap: '8px' },
  title: { fontSize: '20px', fontWeight: 700, letterSpacing: '-0.02em', color: '#1a1a1a' },
  tagline: { color: '#6b6b6b', fontSize: '12px' },
  form: { display: 'flex', flexDirection: 'column', gap: '12px' },
  label: { display: 'block' },
  fieldLabel: { fontSize: '12px', fontWeight: 500, color: '#1a1a1a', marginBottom: '4px', display: 'block' },
  error: { color: '#e85d5d', fontSize: '13px' },
  hint: { color: '#6b6b6b', fontSize: '13px', lineHeight: 1.4 },
  link: {
    background: 'transparent',
    border: 'none',
    color: '#6b6b6b',
    fontSize: '12px',
    textDecoration: 'underline',
    cursor: 'pointer',
    padding: 0,
    alignSelf: 'center',
  },
}
